import { getApiUrl } from '../../config/env'
import { refreshToken as cognitoRefreshToken } from '../auth'
import { getOrMintAnonymousId } from '../auth/anonymous-id'
import { ACCESS_TOKEN_SKEW_SECONDS, accessTokenExpiry } from '../auth/valid-access-token'
import { emitInsufficientCredits } from './insufficient-credits-emitter'
import { emitSessionExpired } from './session-expired-emitter'
import { ApiRequestError, type ApiResponse, type RequestConfig } from './types'

const TOKEN_KEY = 'gisify_token'

// Shared with both auth clients (cognito-client.ts / keycloak-client.ts) — the
// key they persist the refresh token under. Read here as the "can this session
// still recover?" signal on a failed 401 refresh; see the block in `request`.
const REFRESH_TOKEN_KEY = 'gisify_refresh_token'

// Issue #260 — single-flight refresh guard. When the IdToken expires after a
// long idle period, the next click fires multiple parallel requests; without
// this latch, every parallel request would call `cognitoRefreshToken()` and
// race for the new token. The first request wins; others await its result.
// Cleared on resolution (success OR failure) so the next genuine expiry can
// trigger a fresh refresh.
let refreshInFlight: Promise<string | null> | null = null

/**
 * Issue #260 — runs a single Cognito refresh round and caches the in-flight
 * promise across all concurrent callers. After resolve/reject the cache is
 * cleared so subsequent expiries can retry independently.
 */
function getSingleFlightRefresh(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = cognitoRefreshToken().finally(() => {
    refreshInFlight = null
  })

  return refreshInFlight
}

/**
 * Refresh the session token NOW, before a request needs it, sharing the single-flight latch with
 * the 401 path. Persists and returns the new token; null when the refresh failed or threw.
 *
 * For pages outside AuthGuard (the public form fill page), where nothing else refreshes the
 * token: on cloud an expired Bearer on an allowAnonymous route is an authorizer Deny (403), which
 * the refresh-on-401 below never sees. Callers that get null tell a transient failure from a
 * definitive one with {@link hasRefreshableSession}.
 */
export async function refreshSessionToken(): Promise<string | null> {
  let token: string | null = null

  try {
    token = await getSingleFlightRefresh()
  } catch {
    // A THROWN refresh is a transport failure; the identity provider rejected nothing.
  }

  if (token) localStorage.setItem(TOKEN_KEY, token)

  return token
}

/** Refresh first only when the stored token is unreadable or within a minute of expiring. */
export async function refreshSessionTokenIfExpiring(): Promise<void> {
  const token = getAuthToken()
  const exp = token ? accessTokenExpiry(token) : null

  if (exp === null || exp - ACCESS_TOKEN_SKEW_SECONDS <= Date.now() / 1000) await refreshSessionToken()
}

/** False once the auth client dropped the refresh token, which it does only on a DEFINITIVE rejection. */
export function hasRefreshableSession(): boolean {
  return !!localStorage.getItem(REFRESH_TOKEN_KEY)
}

function getTenantId(): string | null {
  // Extract tenant from subdomain (e.g., "tenant1.console.gisify.dev" → "tenant1")
  const hostname = window.location.hostname
  const parts = hostname.split('.')

  if (parts.length >= 3 && parts[1] === 'console') {
    return parts[0]
  }

  // Fallback: check localStorage tenant key
  const storedTenantId = localStorage.getItem('gisify_tenant_id')

  if (storedTenantId) return storedTenantId

  // Fallback: extract from stored user profile
  try {
    const storedUser = localStorage.getItem('gisify_user')

    if (storedUser) {
      const user = JSON.parse(storedUser)

      if (user.tenantId) return user.tenantId
    }
  } catch {
    // ignore parse errors
  }

  return null
}

function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

async function request<T>(
  method: string,
  endpoint: string,
  body?: unknown,
  config: RequestConfig = {},
  // Issue #260 — internal retry flag. Set to `true` after a successful
  // silent token refresh to ensure the retry never recurses.
  _hasRetried = false,
): Promise<ApiResponse<T>> {
  let url = getApiUrl(endpoint)

  // Add query params
  if (config.params) {
    const params = new URLSearchParams()

    Object.entries(config.params).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, String(value))
      }
    })

    const qs = params.toString()

    if (qs) {
      url += `?${qs}`
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...config.headers,
  }

  // Add auth token (skipped for anonymous/central pre-tenant calls)
  if (!config.anonymous) {
    const token = getAuthToken()

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  // Always send anonymous identity (used by mixed-auth endpoints)
  headers['X-Anonymous-Identity'] = getOrMintAnonymousId()

  // Add tenant header (skipped for anonymous/central pre-tenant calls, and for
  // signed-in calls that let the API host decide the tenant)
  if (!config.anonymous && !config.omitTenantHeader) {
    const tenantId = getTenantId()

    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId
    }
  }

  const controller = new AbortController()
  const timeoutId = config.timeout ? setTimeout(() => controller.abort(), config.timeout) : null

  try {
    const response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: config.signal || controller.signal,
    })

    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    // Issue #260 — silent refresh on 401. When the IdToken expires (after
    // an idle period or just from natural rotation), the first click after
    // wake fired with a stale token and the SPA crashed into a blank page
    // because AuthGuard couldn't tell `401 expired` from `401 never-auth`.
    // Now: on 401, try ONE refresh round via the auth client, retry the
    // original request with the new token. If the refresh token is
    // DEFINITIVELY gone, clear the local session — AuthGuard will detect
    // `isAuthenticated=false` on the next render and redirect to
    // /auth/login instead of leaving the user on a blank page.
    if (response.status === 401 && !_hasRetried && getAuthToken()) {
      let newToken: string | null = null

      try {
        newToken = await getSingleFlightRefresh()
      } catch {
        // A THROWN refresh failure is a transport error by construction — the
        // identity provider never got to reject anything. Treat as transient.
        newToken = null
      }

      if (newToken) {
        localStorage.setItem(TOKEN_KEY, newToken)

        return request<T>(method, endpoint, body, config, true)
      }

      // Refresh failed. Only a DEFINITIVE auth failure may end the session:
      // the auth clients drop `gisify_refresh_token` exactly when the provider
      // permanently rejected it (NotAuthorizedException / invalid_grant / user
      // gone) — a transient failure (offline, 5xx, throttle) leaves it in place
      // so a later attempt can still recover. Clearing the session on a
      // transient blip logged people out of a session that was never invalid,
      // so: no refresh material left → drop the stale session; otherwise keep
      // it and let this request's own 401 surface to the caller below.
      // Don't touch X-Anonymous-Identity; it's per-browser, not per-session.
      if (!localStorage.getItem(REFRESH_TOKEN_KEY)) {
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem('gisify_user')

        // Clearing localStorage is not enough: `useAuth` holds the session in a
        // `user` state object and `AuthGuard` reads `isAuthenticated` from it,
        // checking only on mount. Without this the session was dead in storage
        // and alive in memory — the guard kept rendering the app and the 401
        // below surfaced as a page-level error card instead of the login screen,
        // recoverable only by a manual reload. On Keycloak, whose SSO session
        // idles out after 30 minutes, that is a daily event rather than a rarity.
        emitSessionExpired()
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))

      // Centralized out-of-credits surfacing: any 402 insufficient_credits fires
      // the shared modal via the React-free emitter, then still rejects so the
      // call site's own handling runs.
      if (response.status === 402 && errorData.code === 'insufficient_credits') {
        emitInsufficientCredits({
          shortfallCredits: typeof errorData.shortfallCredits === 'number' ? errorData.shortfallCredits : 0,
          balanceCredits: typeof errorData.balanceCredits === 'number' ? errorData.balanceCredits : 0,
          topupUrl: typeof errorData.topupUrl === 'string' ? errorData.topupUrl : undefined,
        })
      }

      throw new ApiRequestError(
        errorData.message || `Request failed with status ${response.status}`,
        errorData.code || 'REQUEST_FAILED',
        response.status,
        errorData.details,
        Array.isArray(errorData.validationErrors) ? errorData.validationErrors : undefined,
        typeof errorData.errorCode === 'string' ? errorData.errorCode : undefined,
        errorData as Record<string, unknown>,
      )
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return { data: null as T }
    }

    const data = await response.json()

    return data as ApiResponse<T>
  } catch (error) {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    if (error instanceof ApiRequestError) {
      throw error
    }

    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiRequestError('Request timed out', 'TIMEOUT', 408)
    }

    throw new ApiRequestError(error instanceof Error ? error.message : 'Network error', 'NETWORK_ERROR', 0)
  }
}

export const api = {
  get<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return request<T>('GET', endpoint, undefined, config)
  },

  post<T>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
    return request<T>('POST', endpoint, body, config)
  },

  put<T>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
    return request<T>('PUT', endpoint, body, config)
  },

  patch<T>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
    return request<T>('PATCH', endpoint, body, config)
  },

  delete<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return request<T>('DELETE', endpoint, config?.data, config)
  },
}

export default api
